import { queryOptions, keepPreviousData } from '@tanstack/react-query';
import { stickingApi } from './sticking.api';
import type { PageIndexListParams, PrintBatchListParams } from '../types';
import { STALE_TIME } from '@/lib/query/stale-times';

export {
  registrationQueries as stickingContainerQueries,
  registrationKeys as stickingContainerKeys
} from '@/features/registration';

export const stickingKeys = {
  all: ['sticking'] as const,
  lists: () => [...stickingKeys.all, 'list'] as const,
  list: (registrationId: number, filters: PageIndexListParams) =>
    [...stickingKeys.lists(), registrationId, filters] as const,
  details: () => [...stickingKeys.all, 'detail'] as const,
  detail: (registrationId: number, pageIndexId: number) =>
    [...stickingKeys.details(), registrationId, pageIndexId] as const,
  metrics: () => [...stickingKeys.all, 'metrics'] as const,
  printBatches: () => [...stickingKeys.all, 'print-batches'] as const,
  printBatchList: (registrationId: number, filters: PrintBatchListParams) =>
    [...stickingKeys.printBatches(), registrationId, filters] as const,
  printBatchMetrics: (registrationId: number) =>
    [...stickingKeys.printBatches(), 'metrics', registrationId] as const
};

export const stickingQueries = {
  pageIndexes: (registrationId: number, filters: PageIndexListParams = {}) =>
    queryOptions({
      queryKey: stickingKeys.list(registrationId, filters),
      queryFn: () => stickingApi.getPageIndexes(registrationId, filters),
      placeholderData: keepPreviousData,
      enabled: registrationId > 0
    }),

  pageIndex: (registrationId: number, pageIndexId: number) =>
    queryOptions({
      queryKey: stickingKeys.detail(registrationId, pageIndexId),
      queryFn: () => stickingApi.getPageIndex(registrationId, pageIndexId),
      enabled: registrationId > 0 && pageIndexId > 0
    }),

  globalMetrics: () =>
    queryOptions({
      queryKey: stickingKeys.metrics(),
      queryFn: () => stickingApi.globalMetrics(),
      staleTime: STALE_TIME.metrics
    }),

  printBatches: (registrationId: number, filters: PrintBatchListParams = {}) =>
    queryOptions({
      queryKey: stickingKeys.printBatchList(registrationId, filters),
      queryFn: () => stickingApi.getPrintBatches(registrationId, filters),
      placeholderData: keepPreviousData,
      enabled: registrationId > 0
    }),

  printBatchMetrics: (registrationId: number) =>
    queryOptions({
      queryKey: stickingKeys.printBatchMetrics(registrationId),
      queryFn: () => stickingApi.getPrintBatchMetrics(registrationId),
      enabled: registrationId > 0
    })
};
